import { useMemo, useState } from "react";
import {
  GiBiceps,
  GiBookAura,
  GiCheckMark,
  GiHammerNails,
  GiPadlock,
  GiRunningShoe,
  GiSandsOfTime,
  GiSpeedometer,
  GiUpgrade,
  GiWoodAxe,
} from "react-icons/gi";
import { useResearches, useStartResearch, ResearchBranches, type ResearchDef } from "../api/research";
import { useCastle } from "../api/castle";
import { Loading } from "./Loading";
import { ErrorView } from "./ErrorView";
import { ScreenBanner } from "../components/ScreenBanner";
import { ResourceBar } from "../components/ResourceBar";
import { CurrencyValue } from "../components/CurrencyIcon";
import { useNow } from "../lib/ticker";
import { formatDuration, formatNumber, secondsUntil } from "../lib/format";
import { hapticImpact, hapticNotification } from "../lib/telegram";

// Icon per branch. Each column header gets one so the screen reads at a
// glance even when scrolled past the headers.
const BranchIcon: Record<string, React.ElementType> = {
  economy:     GiWoodAxe,
  logistics:   GiUpgrade,
  "march-speed": GiRunningShoe,
  "build-speed": GiHammerNails,
  "train-speed": GiSpeedometer,
  tactics:     GiBiceps,
};

export function ResearchScreen({ onBack }: { onBack?: () => void } = {}) {
  const research = useResearches();
  const castle = useCastle();
  const start = useStartResearch();
  const now = useNow(1000);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Partition the flat catalog into per-branch tiers. Sorts by trailing
  // numeric id so "logistics-2" comes after "logistics-1" without relying
  // on insertion order in the API payload.
  const columns = useMemo(() => {
    const list = research.data?.researches ?? [];
    return ResearchBranches.map((b) => ({
      ...b,
      tiers: list
        .filter((r) => r.id.startsWith(b.idPrefix))
        .sort((a, b2) => {
          const an = parseInt(a.id.slice(b.idPrefix.length), 10) || 0;
          const bn = parseInt(b2.id.slice(b.idPrefix.length), 10) || 0;
          return an - bn;
        }),
    }));
  }, [research.data]);

  if (research.isPending || castle.isPending) {
    return <Loading message="Раскрываю свитки..." />;
  }
  if (research.isError || castle.isError) {
    return <ErrorView message="Не получилось загрузить исследования." />;
  }

  const academyLevel = research.data?.academyLevel ?? 0;
  const selected = selectedId
    ? (research.data?.researches ?? []).find((r) => r.id === selectedId) ?? null
    : null;
  const errorMsg =
    (start.error && typeof start.error === "object" && "response" in start.error
      ? ((start.error as { response?: { data?: { error?: string } } }).response?.data?.error ?? null)
      : null);

  return (
    <div className="flex h-full flex-col">
      <ScreenBanner kind="castle" title="Академия" subtitle={`Lvl ${academyLevel}`} />
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="self-start ml-2 mt-2 rounded-lg bg-tg-secondaryBg px-3 py-1 text-sm"
        >
          ← Назад
        </button>
      )}
      <ResourceBar resources={castle.data!.state.resources} safeStorage={castle.data!.safeStorage} />

      {academyLevel === 0 && (
        <div className="mx-3 mt-2 rounded-xl bg-tg-secondaryBg p-3 text-sm text-tg-hint">
          Чтобы запускать исследования, построй Академию (открывается на Замке Lvl 9).
        </div>
      )}

      <div className="flex-1 overflow-x-auto overflow-y-auto px-2 py-2">
        <div className="grid w-max grid-flow-col gap-2">
          {columns.map((c) => {
            const Icon = BranchIcon[c.id] ?? GiBookAura;
            return (
              <div key={c.id} className="flex w-32 flex-col gap-1.5">
                <div className="rounded-lg bg-tg-secondaryBg/70 p-2 text-center">
                  <Icon className="mx-auto" size={20} />
                  <div className="mt-1 text-xs font-medium">{c.name}</div>
                  <div className="text-[10px] text-tg-hint">{c.hint}</div>
                </div>
                {c.tiers.map((r) => (
                  <TierTile
                    key={r.id}
                    tier={r}
                    academyLevel={academyLevel}
                    now={now}
                    onTap={() => {
                      hapticImpact("light");
                      setSelectedId(r.id);
                    }}
                  />
                ))}
              </div>
            );
          })}
        </div>
      </div>

      {selected && (
        <ResearchDetailSheet
          tier={selected}
          academyLevel={academyLevel}
          resources={castle.data!.state.resources}
          activePresent={(research.data?.researches ?? []).some((r) => r.inProgress)}
          error={errorMsg}
          pending={start.isPending}
          onClose={() => {
            setSelectedId(null);
            start.reset();
          }}
          onStart={() =>
            start.mutate(selected.id, {
              onSuccess: () => {
                hapticNotification("success");
                setSelectedId(null);
              },
              onError: () => hapticNotification("error"),
            })
          }
        />
      )}
    </div>
  );
}

function TierTile({
  tier,
  academyLevel,
  now,
  onTap,
}: {
  tier: ResearchDef;
  academyLevel: number;
  now: Date;
  onTap: () => void;
}) {
  const locked = academyLevel < tier.requiresAcademyLevel;
  // Visual state precedence: completed → in progress → locked → available.
  const tone = tier.completed
    ? "bg-yellow-500/20 text-yellow-300 ring-1 ring-yellow-500/40"
    : tier.inProgress
    ? "bg-tg-accent/20 text-tg-text ring-1 ring-tg-accent/50 animate-element-pulse"
    : locked
    ? "bg-tg-secondaryBg/50 text-tg-hint"
    : "bg-tg-secondaryBg text-tg-text active:bg-tg-bg";

  const StatusIcon = tier.completed
    ? GiCheckMark
    : tier.inProgress
    ? GiSandsOfTime
    : locked
    ? GiPadlock
    : null;

  // Tier number = numeric suffix from the id ("logistics-3" → "III").
  const num = parseInt(tier.id.split("-").pop() ?? "0", 10);
  const romanish = ["", "I", "II", "III", "IV", "V"][num] ?? `${num}`;

  return (
    <button
      type="button"
      onClick={onTap}
      className={`flex flex-col items-center rounded-lg px-2 py-2 text-center transition-colors ${tone}`}
    >
      <div className="text-sm font-medium">{romanish}</div>
      {tier.inProgress && tier.finishesAt && (
        <div className="text-[10px] tabular-nums">
          {formatDuration(secondsUntil(tier.finishesAt, now))}
        </div>
      )}
      {locked && !tier.inProgress && !tier.completed && (
        <div className="text-[10px]">Ак. Lvl {tier.requiresAcademyLevel}</div>
      )}
      {StatusIcon && <StatusIcon size={14} className="mt-0.5 opacity-70" />}
    </button>
  );
}

function ResearchDetailSheet({
  tier,
  academyLevel,
  resources,
  activePresent,
  error,
  pending,
  onClose,
  onStart,
}: {
  tier: ResearchDef;
  academyLevel: number;
  resources: { gold: number; wood: number; stone: number; mana: number };
  activePresent: boolean;
  error: string | null;
  pending: boolean;
  onClose: () => void;
  onStart: () => void;
}) {
  const locked = academyLevel < tier.requiresAcademyLevel;
  const canAfford =
    resources.gold >= tier.cost.gold &&
    resources.wood >= tier.cost.wood &&
    resources.stone >= tier.cost.stone &&
    resources.mana >= tier.cost.mana;
  const alreadyDone = tier.completed;
  const blockedByOtherActive = !tier.inProgress && activePresent;
  const canStart =
    !locked && !alreadyDone && !tier.inProgress && !blockedByOtherActive && canAfford && !pending;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60" onClick={onClose}>
      <div
        className="max-h-[90vh] w-full overflow-y-auto rounded-t-2xl bg-tg-bg p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-start justify-between gap-2">
          <div>
            <div className="text-lg font-semibold">{tier.name}</div>
            <div className="mt-0.5 text-xs text-tg-hint">{tier.description}</div>
          </div>
          <button type="button" onClick={onClose} className="text-tg-hint" aria-label="Закрыть">
            ✕
          </button>
        </div>

        <div className="space-y-2 rounded-xl bg-tg-secondaryBg p-3 text-sm">
          <div className="flex items-baseline justify-between">
            <span className="text-tg-hint">Стоимость</span>
            <div className="flex flex-wrap items-center justify-end gap-1.5 text-xs">
              {tier.cost.gold > 0 && (
                <CostLine kind="gold" need={tier.cost.gold} have={resources.gold} />
              )}
              {tier.cost.wood > 0 && (
                <CostLine kind="wood" need={tier.cost.wood} have={resources.wood} />
              )}
              {tier.cost.stone > 0 && (
                <CostLine kind="stone" need={tier.cost.stone} have={resources.stone} />
              )}
              {tier.cost.mana > 0 && (
                <CostLine kind="mana" need={tier.cost.mana} have={resources.mana} />
              )}
            </div>
          </div>
          <div className="flex items-baseline justify-between">
            <span className="text-tg-hint">Длительность</span>
            <span className="tabular-nums">{formatDuration(tier.durationSeconds)}</span>
          </div>
          <div className="flex items-baseline justify-between">
            <span className="text-tg-hint">Академия Lvl</span>
            <span className={locked ? "text-tg-destructive" : "text-tg-text"}>
              {tier.requiresAcademyLevel} (у тебя {academyLevel})
            </span>
          </div>
        </div>

        {alreadyDone && (
          <div className="mt-3 rounded-lg bg-yellow-500/15 p-2 text-sm text-yellow-400">
            Уже изучено.
          </div>
        )}
        {tier.inProgress && (
          <div className="mt-3 rounded-lg bg-tg-accent/15 p-2 text-sm">
            Сейчас в работе. Дождись окончания таймера.
          </div>
        )}
        {locked && !alreadyDone && !tier.inProgress && (
          <div className="mt-3 rounded-lg bg-tg-secondaryBg p-2 text-sm text-tg-hint">
            Подними Академию до Lvl {tier.requiresAcademyLevel}, чтобы открыть это исследование.
          </div>
        )}
        {blockedByOtherActive && (
          <div className="mt-3 rounded-lg bg-tg-secondaryBg p-2 text-sm text-tg-hint">
            Сначала закончи текущее исследование — одно за раз.
          </div>
        )}
        {error && (
          <div className="mt-3 rounded-lg bg-tg-destructive/20 p-2 text-sm text-tg-destructive">
            {error}
          </div>
        )}

        {!alreadyDone && !tier.inProgress && (
          <button
            type="button"
            disabled={!canStart}
            onClick={onStart}
            className="mt-4 w-full rounded-lg bg-tg-button px-3 py-3 text-sm font-medium text-tg-buttonText disabled:opacity-40"
          >
            {pending ? "Запускаю…" : "Начать исследование"}
          </button>
        )}
      </div>
    </div>
  );
}

function CostLine({
  kind,
  need,
  have,
}: {
  kind: "gold" | "wood" | "stone" | "mana";
  need: number;
  have: number;
}) {
  const ok = have >= need;
  return (
    <span className={ok ? "text-tg-text" : "text-tg-destructive"}>
      <CurrencyValue kind={kind} value={formatNumber(need)} />
    </span>
  );
}
